import { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Building, Wallet, Target, CheckCircle2, ArrowRight, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import api from '../api';

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Step 1: Plaid Link State
  const [linkToken, setLinkToken] = useState(null);
  const [plaidLinked, setPlaidLinked] = useState(false);
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [primaryAccountId, setPrimaryAccountId] = useState(null);

  // Step 2: Income State
  const [deposits, setDeposits] = useState([]);
  const [scanningDeposits, setScanningDeposits] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [incomeName, setIncomeName] = useState('Primary Paycheck');
  const [incomeAmount, setIncomeAmount] = useState('4500');
  const [incomeFrequency, setIncomeFrequency] = useState('bi-weekly');
  const [incomeCreated, setIncomeCreated] = useState(false);

  // Step 3: Fund State
  const [discoveredBills, setDiscoveredBills] = useState([]);
  const [scanningBills, setScanningBills] = useState(false);
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [showManualFund, setShowManualFund] = useState(false);
  const [bulkCreationProgress, setBulkCreationProgress] = useState(null);

  const [fundName, setFundName] = useState('Rent / Mortgage');
  const [fundTarget, setFundTarget] = useState('1500');
  const [fundCreated, setFundCreated] = useState(false);

  // Step 4: Preview State
  const [simData, setSimData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(false);

  useEffect(() => {
    // Fetch Link Token on mount
    const fetchLinkToken = async () => {
      try {
        const res = await api.post('/plaid/create-link-token');
        setLinkToken(res.data.link_token);
      } catch (err) {
        console.error('Failed to get link token', err);
      }
    };
    
    // Check if they already linked a bank
    const checkPlaidStatus = async () => {
      try {
        const res = await api.get('/plaid/status');
        if (res.data.linked) {
          setPlaidLinked(true);
          setPlaidAccounts(res.data.accounts || []);
          setPrimaryAccountId(res.data.primary_account_id || null);
        } else {
          fetchLinkToken();
        }
      } catch (err) {
        fetchLinkToken();
      }
    };
    
    checkPlaidStatus();
  }, []);

  useEffect(() => {
    if (step === 2 && plaidLinked && deposits.length === 0) {
      const fetchDeposits = async () => {
        setScanningDeposits(true);
        try {
          const res = await api.get('/plaid/deposits');
          if (res.data.deposits && res.data.deposits.length > 0) {
            setDeposits(res.data.deposits);
            setShowManualEntry(false);
          } else {
            setShowManualEntry(true);
          }
        } catch (err) {
          console.error(err);
          setShowManualEntry(true);
        } finally {
          setScanningDeposits(false);
        }
      };
      fetchDeposits();
    }
  }, [step, plaidLinked, deposits.length]);

  useEffect(() => {
    if (step === 3 && plaidLinked && discoveredBills.length === 0 && !scanningBills) {
      const fetchBills = async () => {
        setScanningBills(true);
        try {
          const res = await api.get('/plaid/analyze-history');
          if (res.data.suggestions && res.data.suggestions.length > 0) {
            setDiscoveredBills(res.data.suggestions);
            // Auto-select none by default
            setSelectedBills(new Set());
            setShowManualFund(false);
          } else {
            setShowManualFund(true);
          }
        } catch (err) {
          console.error(err);
          setShowManualFund(true);
        } finally {
          setScanningBills(false);
        }
      };
      fetchBills();
    }
  }, [step, plaidLinked, discoveredBills.length]);

  useEffect(() => {
    if (step === 4 && !simData && !generating) {
      const generateRules = async () => {
        setGenerating(true);
        setGenerationError(false);
        try {
          await api.post('/allocations/global-smart-allocate');
          const incomesRes = await api.get('/incomes');
          const allocationsRes = await api.get('/allocations');
          
          if (incomesRes.data.length > 0) {
            const primaryIncome = incomesRes.data[0];
            const primaryIncomeId = primaryIncome.id;
            const incomeAmount = parseFloat(primaryIncome.amount) || 0;
            
            // Filter allocations for this specific income
            const primaryRules = allocationsRes.data.filter(a => a.income_id === primaryIncomeId);
            
            // Construct simulated preview list deterministically
            let remaining = incomeAmount;
            const breakdown = primaryRules.map(rule => {
              let allocated = 0;
              if (rule.type === 'fixed') {
                allocated = parseFloat(rule.amount) || 0;
              } else if (rule.type === 'percentage') {
                allocated = (parseFloat(rule.amount) / 100) * incomeAmount;
              }
              remaining -= allocated;
              return {
                fund_name: rule.fund_name,
                allocatedAmount: allocated,
                color: rule.color,
                type: rule.type
              };
            });
            
            setSimData({
              initialAmount: incomeAmount,
              breakdown,
              unallocatedAmount: remaining
            });
          } else {
            setGenerationError(true);
          }
        } catch (err) {
          console.error(err);
          setGenerationError(true);
        } finally {
          setGenerating(false);
        }
      };
      generateRules();
    }
  }, [step, simData, generating]);

  const onSuccess = useCallback(async (public_token, metadata) => {
    setLoading(true);
    try {
      await api.post('/plaid/exchange-public-token', { public_token });
      const statusRes = await api.get('/plaid/status');
      setPlaidLinked(true);
      setPlaidAccounts(statusRes.data.accounts || []);
      setPrimaryAccountId(statusRes.data.primary_account_id || null);
    } catch (err) {
      console.error('Exchange failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
  });

  const handleSetPrimaryAccount = async (accountId) => {
    setLoading(true);
    try {
      await api.post('/plaid/set-primary-account', { accountId });
      setPrimaryAccountId(accountId);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIncome = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/incomes', {
        name: incomeName,
        nickname: 'My Paycheck',
        amount: parseFloat(incomeAmount),
        frequency: incomeFrequency
      });
      setIncomeCreated(true);
      setStep(3);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save income source');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFund = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/funds', {
        name: fundName,
        type: 'expense',
        target_amount: parseFloat(fundTarget),
        color: '#3b82f6'
      });
      setFundCreated(true);
      setStep(4);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create bucket');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSelectedBills = async () => {
    const billsToCreate = Array.from(selectedBills).map(idx => discoveredBills[idx]);
    if (billsToCreate.length === 0) {
      toast.error("Please select at least one bill, or enter manually.");
      return;
    }
    
    setLoading(true);
    let successCount = 0;
    
    for (let i = 0; i < billsToCreate.length; i++) {
      const bill = billsToCreate[i];
      setBulkCreationProgress(`Creating ${i + 1} of ${billsToCreate.length}...`);
      try {
        await api.post('/funds', {
          name: bill.merchant,
          type: 'expense',
          target_amount: bill.averageAmount,
          due_day: bill.estimatedDueDay,
          color: '#3b82f6',
          merchants: [bill.merchant] // Bind pattern for auto-sync
        });
        successCount++;
      } catch (err) {
        console.error(`Failed to create bucket for ${bill.merchant}`, err);
      }
    }
    
    setBulkCreationProgress(null);
    setLoading(false);
    
    if (successCount > 0) {
      setFundCreated(true);
      setStep(4);
    } else {
      toast.error("Failed to create any buckets. Please try manual entry.");
    }
  };

  const toggleBillSelection = (index) => {
    const newSelection = new Set(selectedBills);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedBills(newSelection);
  };

  const handleFinishAutopilot = async () => {
    setLoading(true);
    try {
      // 1. Run Global Smart Allocate
      await api.post('/allocations/global-smart-allocate');
      
      // 2. Complete Onboarding
      await api.post('/auth/complete-onboarding');
      const user = JSON.parse(localStorage.getItem('thread_user'));
      user.has_completed_onboarding = true;
      localStorage.setItem('thread_user', JSON.stringify(user));
      
      window.location.href = '/';
    } catch (err) {
      console.error(err);
      toast.error('Error during Autopilot setup');
      setLoading(false);
    }
  };

  const handleFinishManual = async () => {
    setLoading(true);
    try {
      await api.post('/auth/complete-onboarding');
      const user = JSON.parse(localStorage.getItem('thread_user'));
      user.has_completed_onboarding = true;
      localStorage.setItem('thread_user', JSON.stringify(user));
      
      window.location.href = '/';
    } catch (err) {
      console.error(err);
      toast.error('Error finishing setup');
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-color)', backgroundImage: 'var(--bg-gradient)', alignItems: 'center', padding: 'var(--spacing-xl) 20px', overflowY: 'auto' }}>
      
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 className="text-gradient" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', margin: 0 }}>
          <Logo size={32} /> Thread Finance
        </h1>
        <p className="text-muted" style={{ marginTop: '0.5rem' }}>Set up your virtual ledger in 3 simple steps</p>
      </div>

      <div className="glass animate-fade-in" style={{ width: '100%', maxWidth: '500px', padding: '0', overflow: 'hidden' }}>
        
        {/* Progress Bar */}
        <div style={{ display: 'flex', background: 'rgba(0, 0, 0, 0.3)', borderBottom: '1px solid var(--surface-border)', height: '4px' }}>
          {[1, 2, 3, 4].map(s => (
            <div key={s} style={{ 
              flex: 1, 
              height: '100%', 
              background: step >= s ? 'linear-gradient(90deg, var(--primary), var(--secondary))' : 'transparent',
              boxShadow: step >= s ? '0 0 10px rgba(59, 130, 246, 0.5)' : 'none',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }} />
          ))}
        </div>

        <div style={{ padding: 'var(--spacing-xl)' }}>
          
          {/* STEP 1: Connect Bank */}
          {step === 1 && (
            <div className="animate-fade-in text-center">
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '16px', 
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(79, 70, 229, 0.05) 100%)', 
                  border: '1px solid rgba(59, 130, 246, 0.25)', 
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.1)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  color: 'var(--primary)' 
                }}>
                  <Building size={30} />
                </div>
              </div>
              <h2 style={{ marginBottom: '1rem' }}>Connect Your Bank</h2>
              <p className="text-muted" style={{ marginBottom: '2rem', lineHeight: '1.6' }}>
                Thread Finance is a smart "virtual envelope" system. We securely sync with your primary checking account to detect paychecks and automatically track your splits. <strong>We do not move your real money.</strong>
              </p>
              
              {plaidLinked ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--success)', fontWeight: 600, marginBottom: '1rem' }}>
                    <CheckCircle2 size={20} /> Bank Connected Successfully
                  </div>
                  
                  <div style={{ width: '100%', background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)', textAlign: 'left', marginBottom: '1rem' }}>
                    <h4 style={{ margin: '0 0 1rem 0' }}>Select Primary Autopilot Account</h4>
                    {!primaryAccountId && (
                       <div style={{ padding: '0.75rem', background: 'rgba(234, 179, 8, 0.1)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(234, 179, 8, 0.3)', color: 'var(--warning)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                         <strong>⚠️ Action Required:</strong> Please select a Primary Autopilot account from the list below. Autopilot will exclusively scan this account for paychecks and bills.
                       </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {plaidAccounts.map(acc => (
                        <div key={acc.account_id} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          background: primaryAccountId === acc.account_id ? 'rgba(59, 130, 246, 0.04)' : 'rgba(0,0,0,0.15)', 
                          padding: '1.1rem 1.25rem', 
                          borderRadius: 'var(--radius-md)', 
                          border: primaryAccountId === acc.account_id ? '1px solid var(--primary)' : '1px solid var(--surface-border)',
                          boxShadow: primaryAccountId === acc.account_id ? '0 0 15px rgba(59, 130, 246, 0.1)' : 'none',
                          transition: 'all 0.2s ease'
                        }}>
                          <div>
                            <p style={{ margin: '0 0 0.25rem 0', fontWeight: 'bold' }}>{acc.bankName} <span style={{ opacity: 0.5, fontSize: '0.85rem' }}>({acc.type})</span></p>
                            <span className="text-muted" style={{ fontSize: '0.9rem' }}>Account ending in •••• {acc.mask}</span>
                          </div>
                          <div>
                            {primaryAccountId === acc.account_id ? (
                              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <CheckCircle2 size={16} /> Primary
                              </span>
                            ) : (
                              <button className="btn btn-glass" onClick={() => handleSetPrimaryAccount(acc.account_id)} disabled={loading} style={{ fontSize: '0.85rem' }}>
                                Set Primary
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!primaryAccountId} style={{ width: '100%', justifyContent: 'center' }}>
                    Continue to Step 2 <ArrowRight size={18} />
                  </button>
                </div>
              ) : (
                <button 
                  className="btn btn-primary" 
                  onClick={() => open()} 
                  disabled={loading || !ready}
                  style={{ width: '100%', justifyContent: 'center', padding: '1rem' }}
                >
                  {loading ? <Loader2 size={20} className="spin" /> : 'Securely Link Bank Account'}
                </button>
              )}
            </div>
          )}

          {/* STEP 2: Income */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><ArrowLeft size={20} /></button>
                <h2 style={{ margin: 0 }}>Define Your Paycheck</h2>
              </div>
              <p className="text-muted" style={{ marginBottom: '2rem' }}>
                Tell us about your primary income. When this hits your checking account, we'll auto-route it into your buckets.
              </p>

              {!showManualEntry && (scanningDeposits || deposits.length > 0) ? (
                <div className="animate-fade-in">
                  <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, color: 'var(--primary)' }}>Select Detected Paycheck</h4>
                    <button className="btn btn-glass" onClick={() => setShowManualEntry(true)} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
                      Enter Manually
                    </button>
                  </div>
                  
                  {scanningDeposits ? (
                    <div style={{ textAlign: 'center', padding: '3rem 1rem', background: 'rgba(0,0,0,0.2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)' }}>
                      <Loader2 size={32} className="spin" style={{ color: 'var(--primary)', margin: '0 auto 1rem auto' }} />
                      <div className="text-muted">Scanning your connected account for recent direct deposits...</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {deposits.map((dep, idx) => (
                        <div 
                          key={idx}
                          className="glass"
                          style={{ 
                            padding: '1.1rem 1.25rem', 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            cursor: 'pointer', 
                            border: '1px solid var(--surface-border)',
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: 'var(--radius-md)',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.border = '1px solid rgba(59, 130, 246, 0.4)';
                            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.03)';
                            e.currentTarget.style.boxShadow = '0 0 15px rgba(59, 130, 246, 0.08)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.border = '1px solid var(--surface-border)';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          onClick={() => {
                            setIncomeName(dep.name);
                            setIncomeAmount(Math.abs(dep.amount).toString());
                            setShowManualEntry(true);
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.25rem', fontSize: '0.95rem' }}>{dep.name}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Detected {new Date(dep.date).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})}</div>
                          </div>
                          <div style={{ color: 'var(--success)', fontWeight: 700, fontSize: '1.05rem' }}>
                            +${Math.abs(dep.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleCreateIncome} className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {deposits.length > 0 && (
                    <button 
                      type="button" 
                      onClick={() => setShowManualEntry(false)} 
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'flex-start', padding: 0, fontWeight: 500 }}
                    >
                      <ArrowLeft size={16} /> Back to Detected Deposits
                    </button>
                  )}
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Employer / Deposit Name</label>
                    <input type="text" value={incomeName} onChange={e => setIncomeName(e.target.value)} required placeholder="e.g. ACME Corp Payroll" />
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Typical Amount</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                        <input type="number" step="0.01" value={incomeAmount} onChange={e => setIncomeAmount(e.target.value)} required style={{ paddingLeft: '2rem' }} />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Frequency</label>
                      <select value={incomeFrequency} onChange={e => setIncomeFrequency(e.target.value)} required>
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="semi-monthly">Semi-Monthly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}>
                    {loading ? <Loader2 size={20} className="spin" /> : <>Save Income <ArrowRight size={18} /></>}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* STEP 3: First Fund */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><ArrowLeft size={20} /></button>
                <h2 style={{ margin: 0 }}>Create your Buckets</h2>
              </div>
              <p className="text-muted" style={{ marginBottom: '2rem' }}>
                Buckets act as virtual envelopes. We scanned your bank history for recurring bills to get you started quickly.
              </p>

              {!showManualFund && (scanningBills || discoveredBills.length > 0) ? (
                <div className="animate-fade-in">
                  <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, color: 'var(--primary)' }}>Detected Recurring Bills</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {!scanningBills && discoveredBills.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => {
                          if (selectedBills.size === discoveredBills.length) {
                            setSelectedBills(new Set());
                          } else {
                            setSelectedBills(new Set(discoveredBills.map((_, i) => i)));
                          }
                        }}>
                          <input 
                            type="checkbox" 
                            checked={selectedBills.size === discoveredBills.length && discoveredBills.length > 0} 
                            readOnly
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{selectedBills.size === discoveredBills.length ? 'Unselect All' : 'Select All'}</span>
                        </div>
                      )}
                      <button className="btn btn-glass" onClick={() => setShowManualFund(true)} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
                        Enter Manually
                      </button>
                    </div>
                  </div>
                  
                  {scanningBills ? (
                    <div style={{ textAlign: 'center', padding: '3rem 1rem', background: 'rgba(0,0,0,0.2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)' }}>
                      <Loader2 size={32} className="spin" style={{ color: 'var(--primary)', margin: '0 auto 1rem auto' }} />
                      <div className="text-muted" style={{ marginBottom: '0.5rem' }}>Scanning 90 days of history for recurring bills...</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>This usually takes a few seconds.</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div className="custom-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '350px', overflowY: 'auto', paddingRight: '0.35rem' }}>
                        {discoveredBills.map((bill, idx) => {
                          const isSelected = selectedBills.has(idx);
                          return (
                            <div 
                              key={idx}
                              style={{ 
                                padding: '1.1rem 1.25rem', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                cursor: 'pointer', 
                                background: isSelected ? 'rgba(79, 70, 229, 0.04)' : 'rgba(255, 255, 255, 0.02)',
                                border: isSelected ? '1px solid var(--primary)' : '1px solid var(--surface-border)',
                                borderRadius: 'var(--radius-md)',
                                boxShadow: isSelected ? '0 0 15px rgba(59, 130, 246, 0.1)' : 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
                                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.border = '1px solid var(--surface-border)';
                                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                                }
                              }}
                              onClick={() => toggleBillSelection(idx)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ 
                                  width: '20px', 
                                  height: '20px', 
                                  borderRadius: '6px', 
                                  border: isSelected ? 'none' : '1.5px solid var(--text-muted)',
                                  background: isSelected ? 'var(--primary)' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  flexShrink: 0
                                }}>
                                  {isSelected && <CheckCircle2 size={14} />}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.25rem', fontSize: '0.95rem' }}>{bill.merchant}</div>
                                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Found {bill.occurrences} times • Due ~{bill.estimatedDueDay}th</div>
                                </div>
                              </div>
                              <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '1.05rem' }}>
                                ${bill.averageAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <button 
                        className="btn btn-primary" 
                        onClick={handleCreateSelectedBills}
                        disabled={loading || selectedBills.size === 0} 
                        style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}
                      >
                        {loading ? (
                          <>
                            <Loader2 size={20} className="spin" /> 
                            {bulkCreationProgress || 'Creating...'}
                          </>
                        ) : (
                          <>Create {selectedBills.size} Buckets <ArrowRight size={18} /></>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleCreateFund} className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {discoveredBills.length > 0 && (
                    <button 
                      type="button" 
                      onClick={() => setShowManualFund(false)} 
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'flex-start', padding: 0, fontWeight: 500 }}
                    >
                      <ArrowLeft size={16} /> Back to Detected Bills
                    </button>
                  )}
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Bucket Name</label>
                    <input type="text" value={fundName} onChange={e => setFundName(e.target.value)} required placeholder="e.g. Rent / Mortgage" />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Target Amount (Per Month)</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                      <input type="number" step="0.01" value={fundTarget} onChange={e => setFundTarget(e.target.value)} required style={{ paddingLeft: '2rem' }} />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}>
                    {loading ? <Loader2 size={20} className="spin" /> : <>Create Bucket <ArrowRight size={18} /></>}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* STEP 4: Complete / Preview */}
          {step === 4 && (
            <div className="animate-fade-in">
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <h2 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  {generating ? <Loader2 className="spin" size={24} /> : <CheckCircle2 size={24} color="var(--success)" />}
                  {generating ? 'Generating Your Strategy...' : 'Your Autopilot is Ready'}
                </h2>
                <p className="text-muted" style={{ lineHeight: '1.6' }}>
                  {generating ? 'Thread Finance is analyzing your targets and constructing the perfect routing logic...' : 'We have successfully built your routing rules. Here is how your money will flow.'}
                </p>
              </div>

              {!generating && generationError && (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: 'var(--radius-md)' }}>
                  <p style={{ color: 'var(--danger)', marginBottom: '1rem' }}>We encountered an error generating your rules.</p>
                  <button className="btn btn-outline" onClick={() => setStep(3)}>Go Back and Try Again</button>
                  <button className="btn btn-primary" onClick={handleFinishManual} style={{ marginLeft: '1rem' }}>Skip to Dashboard</button>
                </div>
              )}

              {!generating && !generationError && simData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div className="glass" style={{ padding: '1.5rem', background: 'rgba(255, 255, 255, 0.01)', border: '1px solid var(--surface-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Initial Paycheck</span>
                      <strong style={{ fontSize: '1.15rem', color: 'var(--text-main)' }}>${(simData.initialAmount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                    </div>
                    
                    <div style={{ color: 'var(--primary)', margin: '1.25rem 0 0.75rem', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Transfer Steps</div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', position: 'relative' }}>
                      {/* Vertical connector line behind step circles */}
                      <div style={{ position: 'absolute', left: '19px', top: '10px', bottom: '10px', width: '2px', background: 'rgba(255, 255, 255, 0.05)', zIndex: 0 }} />

                      {simData.breakdown.map((a, i) => (
                        <div key={i} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          padding: '0.6rem 0.75rem',
                          background: 'rgba(255, 255, 255, 0.01)',
                          borderRadius: 'var(--radius-sm)',
                          position: 'relative',
                          zIndex: 1
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                            <div style={{ 
                              width: '10px', 
                              height: '10px', 
                              borderRadius: '50%', 
                              backgroundColor: a.color || 'var(--primary)',
                              boxShadow: `0 0 8px ${a.color || 'var(--primary)'}80`,
                              margin: '0 4px'
                            }} />
                            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {a.fund_name || a.name || 'Unnamed Bucket'}
                            </span>
                            {a.type === 'percentage' && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'rgba(255, 255, 255, 0.04)', padding: '1px 6px', borderRadius: '4px' }}>
                                %
                              </span>
                            )}
                          </span>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-main)' }}>
                            -${(a.allocatedAmount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem', marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: '1.05rem' }}>
                      <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>Safe to Spend (Leftover)</span>
                      <strong style={{ color: simData.unallocatedAmount < 0 ? 'var(--danger)' : 'var(--success)' }}>
                        ${(simData.unallocatedAmount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </strong>
                    </div>
                  </div>

                  {simData.unallocatedAmount < 0 && (
                    <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <strong>Note:</strong> Your bucket targets exceed your paycheck. You can adjust your routing priorities on your dashboard.
                    </div>
                  )}

                  <button 
                    className="btn btn-primary" 
                    onClick={handleFinishManual} 
                    disabled={loading}
                    style={{ width: '100%', justifyContent: 'center', padding: '1.25rem', fontSize: '1.1rem', background: 'linear-gradient(to right, #4f46e5, #7c3aed)', border: 'none', boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)' }}
                  >
                    {loading ? <Loader2 size={20} className="spin" /> : 'Looks Good! Go to Dashboard'}
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
                    You can always edit this later.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
